import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Loader2, User, Lock, Shield, Smartphone } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "../../lib/auth";
import { getErr } from "../../lib/api";

export default function AdminLogin() {
  const { user, login, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to={isAdmin ? "/admin" : "/portal"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(identifier.trim(), password);
      toast.success(`Welcome${data.user?.name ? `, ${data.user.name}` : ""}!`);
      navigate(data.redirect || "/admin");
    } catch (err) {
      toast.error(getErr(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFCFB]" data-testid="admin-login-page">
      <Toaster richColors position="top-right" />

      <div className="hidden lg:flex p-12 bg-[#121C17] text-[#FDFCFB] flex-col justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#1E4D3B] grid place-items-center font-display font-semibold">F</div>
          <span className="font-display text-xl font-semibold">FinSahayak</span>
        </Link>
        <div>
          <span className="label-eyebrow" style={{ color: "#D17B5D" }}>One login. Everyone.</span>
          <h2 className="mt-3 text-4xl font-display font-medium tracking-tight">Sign in to your space.</h2>
          <p className="mt-4 text-white/70 max-w-md">Whether you're a customer tracking your service, an operator managing leads, or the admin running the show — one door, one login.</p>
          <ul className="mt-8 space-y-3 text-sm text-white/80">
            <li className="flex items-center gap-3"><span className="w-9 h-9 rounded-full bg-white/10 grid place-items-center"><Smartphone size={14} /></span> Customers & leads — use your <strong>mobile number</strong></li>
            <li className="flex items-center gap-3"><span className="w-9 h-9 rounded-full bg-white/10 grid place-items-center"><Shield size={14} /></span> Admins & operators — use your <strong>registered login ID</strong></li>
          </ul>
        </div>
        <p className="text-xs text-white/40">© FinSahayak Technologies Pvt. Ltd.</p>
      </div>

      <div className="grid place-items-center p-6 sm:p-10">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="admin-login-form">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl bg-[#1E4D3B] grid place-items-center text-[#FDFCFB] font-display font-semibold">F</div>
            <span className="font-display text-xl font-semibold text-[#121C17]">FinSahayak</span>
          </Link>
          <span className="label-eyebrow">Sign In</span>
          <h1 className="mt-3 text-3xl font-display font-medium text-[#121C17]">Welcome back.</h1>
          <p className="mt-2 text-sm text-[#5C6661]">Enter your login ID and password to continue.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="label-eyebrow text-[10px]">Login ID</label>
              <div className="relative mt-2">
                <User size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C6661]" />
                <input
                  type="text"
                  required
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  data-testid="admin-login-email"
                  placeholder="Mobile or email"
                  autoComplete="username"
                  className="w-full bg-white border border-[#E5E2DC] rounded-xl pl-10 pr-4 py-3 text-sm focus:border-[#1E4D3B]"
                />
              </div>
            </div>
            <div>
              <label className="label-eyebrow text-[10px]">Password</label>
              <div className="relative mt-2">
                <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C6661]" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password"
                  autoComplete="current-password"
                  className="w-full bg-white border border-[#E5E2DC] rounded-xl pl-10 pr-4 py-3 text-sm focus:border-[#1E4D3B]"
                />
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="admin-login-submit"
              className="btn-primary rounded-full w-full px-6 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : null} Sign in
            </button>
          </div>

          <p className="mt-6 text-xs text-center text-[#5C6661]">
            No account yet? <Link to="/services" className="text-[#1E4D3B] font-medium hover:underline">Start a service →</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
